import { Button, message, Tabs } from "antd";
import React, { useState, useEffect, useRef } from "react";
import ChiefComplaintsForm from "../OpFormComponents/ChiefComplaintsForm";
import VitalsForm from "../OpFormComponents/VitalsForm";
import OPExaminationForm from "../OpFormComponents/OPExaminationForm";
import OpFunctionalDiagnosis from "./OpFunctionalDiagnosis";
import CardioTherapyForm from "./CardioTherapyForm";
import ReviewNotes from "../OpFormComponents/ReviewNotes";
import OpExerciseForm from "../OpFormComponents/OpExercise/OpExerciseForm";
import CardioSummary from "./CardioSummary";
import OpHistoryForm from "../OpFormComponents/OpHistoryForm";
import CardioHistory from "./CardioHistory";
import { opServiceAPI } from "../../../../../services/opphysio.services";
import { theme } from "antd";
import PhysioScalesOp from "../../../../IpEMR/InspectionForms/Examination/PhysioScales/PhysioScalesOp";
import { opConfirmModal } from "../../opConfirmModal";
import "../../OPForms.css";
const CARDIO_SCALE_TAB_MAP = {
  5: "MMT",
  6: "ROM",
  7: "BarthelIndex",
  8: "RPE",
  9: "SixMinuteWalkTest",
  10: "PeakFlowMeterTest",
};
const CARDIO_SCALE_KEY_NORMALIZER = {
  rass: "ras_scale",
  fingergriptest: "finger_grip_test",
  coordinationtest: "coordination",
  trunkcontroltest: "tcms",
  barthelindex: "barthel",
  rom: "rom",
  rpe: "rpe",
  sixminutewalktest: "six_min_walk",
  peakflowmetertest: "peak_flow_meter_test",
};

const tabItems = [
  { label: "Chief Complaints", key: "1" },
  { label: "History", key: "2" },
  { label: "Vitals", key: "3" },
  { label: "Examination", key: "4" },
  { label: "MMT", key: "5" },
  { label: "ROM", key: "6" },
  { label: "Barthel Index", key: "7" },
  { label: "RPE", key: "8" },
  { label: "six minutes walk test", key: "9" },
  { label: "Peak Flow Meter", key: "10" },
  { label: "Functional Diagnosis", key: "11" },
  { label: "Therapy", key: "12" },
  { label: "Exercise", key: "13" },
  { label: "Review Notes", key: "14" },
  { label: "Summary", key: "15" },
];
const getTabLabel = (key) => {
  const tab = tabItems.find((item) => item.key === key);
  return tab?.label || "Form";
};
const getFirstFilledTab = (data) => {
  if (data?.chief_complaints) return "1";
  if (data?.history) return "2";
  if (data?.vitals) return "3";
  if (data?.examination) return "4";
  if (data?.mmt) return "5";
  if (data?.rom) return "6";
  if (data?.barthel_index) return "7";
  if (data?.rpe) return "8";
  if (data?.six_min_walk) return "9";
  if (data?.peak_flow_meter) return "10";
  if (data?.functional_diagnosis) return "11";
  if (data?.therapy) return "12";
  if (data?.exercise) return "13";
  if (data?.review_notes) return "14";
  if (data?.summary) return "15";
  return "1";
};

const CardiopulmonaryForm = ({
  admissionId,
  handleClose,
  formData: initialData = {},
  onSave,
  patientData,
}) => {
  const [formData, setFormData] = useState(initialData || {});
  const lastSavedRef = useRef(initialData || {});
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const { token } = theme.useToken();
  const [activeScaleKey, setActiveScaleKey] = useState(null);
  const scaleSaveRef = useRef(null);
  const [scalesId, setScalesId] = useState(null);

  const onValuesChange = (key, changedValues, meta) => {
    if (key === "exercise" && Array.isArray(changedValues)) {
      setFormData((prev) => ({
        ...prev,
        exercise: changedValues,
      }));
      if (meta?.hydrateOnly) {
        lastSavedRef.current = {
          ...(lastSavedRef.current || {}),
          exercise: changedValues,
        };
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          ...changedValues,
        },
      }));
      if (meta?.hydrateOnly) {
        lastSavedRef.current = {
          ...(lastSavedRef.current || {}),
          [key]: {
            ...((lastSavedRef.current || {})[key] || {}),
            ...changedValues,
          },
        };
      }
    }
    if (!meta?.hydrateOnly) {
      setIsDirty(true);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
  const handleTabChange = (nextKey) => {
    const nextScaleKey = CARDIO_SCALE_TAB_MAP[nextKey] || null;

    if (isDirty) {
      opConfirmModal({
        title: "Unsaved changes",
        content: "Save and proceed?",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: "#fff",
          },
        },
        onOk: async () => {
          let payload = { ...formData };

          if (activeScaleKey && scaleSaveRef.current) {
            const scaleData = await scaleSaveRef.current.saveScale();

            if (scaleData) {
              const normalizedKey =
                CARDIO_SCALE_KEY_NORMALIZER[activeScaleKey.toLowerCase()] ||
                activeScaleKey.toLowerCase();

              payload[normalizedKey] = {
                ...(formData[normalizedKey] || {}),
                ...scaleData,
              };
            }
          }

          await submitWithPayload({
            ...payload,
            admission_id: admissionId || formData?.admission_id,
          });

          setActiveTab(nextKey);
          setActiveScaleKey(nextScaleKey);
          setIsDirty(false);
        },
        onCancel: () => {
          setFormData(lastSavedRef.current || initialData || {});
          setIsDirty(false);
          setActiveTab(nextKey);
          setActiveScaleKey(nextScaleKey);
        },
      });
    } else {
      setActiveTab(nextKey);
      setActiveScaleKey(nextScaleKey);
    }
  };

  const handleCloseWithCheck = () => {
    const actuallyDirty =
      isDirty ||
      JSON.stringify(formData || {}) !==
        JSON.stringify(lastSavedRef.current || initialData || {});

    if (actuallyDirty) {
      opConfirmModal({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Do you want to save these changes before closing?",
        okText: "Save & Close",
        cancelText: "Close without saving",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: "#fff",
          },
        },
        onOk: async () => {
          const actualAdmissionId = admissionId || formData?.admission_id;
          if (!actualAdmissionId) {
            message.error("Admission ID is required.");
            return;
          }

          let payload = { ...formData, admission_id: actualAdmissionId };

          if (activeScaleKey && scaleSaveRef.current) {
            const scaleData = await scaleSaveRef.current.saveScale();
            if (scaleData) {
              const normalizedKey =
                CARDIO_SCALE_KEY_NORMALIZER[activeScaleKey.toLowerCase()] ||
                activeScaleKey.toLowerCase();

              payload[normalizedKey] = {
                ...(payload[normalizedKey] || {}),
                ...scaleData,
              };
            }
          }

          await submitWithPayload(payload);
          setIsDirty(false);
          handleClose();
        },
        onCancel: () => {
          setFormData(lastSavedRef.current || initialData || {});
          setIsDirty(false);
          handleClose();
        },
      });
    } else {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    try {
      const actualAdmissionId = admissionId || formData?.admission_id;
      if (!actualAdmissionId) {
        message.error("Admission ID is required.");
        return;
      }

      const payload = { ...formData, admission_id: actualAdmissionId };

      delete payload.TrunkControlTest;
      delete payload.barthel_index;
      const method = payload?.id ? "PATCH" : "POST";

      const response = await opServiceAPI("cardiopulmonary", payload, method);

      const returned = response?.data?.data;

      message.success(
        method === "POST"
          ? "Cardiopulmonary EMR saved."
          : "Cardiopulmonary EMR updated."
      );

      setFormData((prev) => ({
        ...prev,
        ...payload,
        ...returned,
        functional_diagnosis: {
          ...(payload?.functional_diagnosis ||
            prev?.functional_diagnosis ||
            {}),
          ...(returned?.functional_diagnosis || {}),
        },
        id: returned?.id ?? prev?.id,
      }));
      lastSavedRef.current = {
        ...(lastSavedRef.current || {}),
        ...payload,
        ...returned,
        id: returned?.id ?? formData?.id,
      };

      if (onSave && activeTab === "15") {
        onSave(payload);
      }

      setIsDirty(false);
    } catch (error) {
      console.error("API error:", error.response || error);
      message.error(error?.response?.data?.message || "Failed to save form.");
    }
  };
  const submitWithPayload = async (payload) => {
    try {
      if (payload && typeof payload === "object") {
        delete payload.TrunkControlTest;
        delete payload.barthel_index;
      }
      const method = payload?.id ? "PATCH" : "POST";

      const response = await opServiceAPI("cardiopulmonary", payload, method);
      const returned = response?.data?.data;

      setFormData((prev) => ({
        ...prev,
        ...payload,
        ...returned,
        functional_diagnosis: {
          ...(payload?.functional_diagnosis ||
            prev?.functional_diagnosis ||
            {}),
          ...(returned?.functional_diagnosis || {}),
        },
        id: returned?.id ?? prev?.id,
      }));
      lastSavedRef.current = {
        ...(lastSavedRef.current || {}),
        ...payload,
        ...returned,
        id: returned?.id ?? formData?.id,
      };

      const tabLabel = getTabLabel(activeTab);
      message.success(`${tabLabel} saved successfully`);

      setIsDirty(false);
    } catch (error) {
      message.error("Failed to save Cardiopulmonary EMR.");
    }
  };

  const handleButton = async () => {
    const actualAdmissionId = admissionId || formData?.admission_id;
    if (!actualAdmissionId) {
      message.error("Admission ID is required.");
      return;
    }

    if (activeTab === "15") {
      await handleSubmit();
      handleClose();
      return;
    }

    let payload = {
      ...formData,
      admission_id: actualAdmissionId,
    };

    if (activeScaleKey && scaleSaveRef.current) {
      const scaleData = await scaleSaveRef.current.saveScale();

      if (scaleData) {
        const normalizedKey =
          CARDIO_SCALE_KEY_NORMALIZER[activeScaleKey.toLowerCase()] ||
          activeScaleKey.toLowerCase();

        payload[normalizedKey] = {
          ...(formData[normalizedKey] || {}),
          ...scaleData,
        };
      }
    }

    await submitWithPayload(payload);

    const nextTab = (parseInt(activeTab) + 1).toString();
    setActiveTab(nextTab);
    setActiveScaleKey(CARDIO_SCALE_TAB_MAP[nextTab] || null);
  };

  const renderTab = () => {
    if (activeScaleKey) {
      return (
        <PhysioScalesOp
          key={activeScaleKey}
          ref={scaleSaveRef}
          admission={admissionId}
          activeScaleKey={activeScaleKey}
          scalesId={scalesId}
          setScalesId={setScalesId}
          editData={formData}
          notifyOnSave={true}
          isNeuro={true}
          onClose={handleCloseWithCheck}
          onScaleDirty={() => setIsDirty(true)}
          onScalesSubmit={(key, data) => {
            const normalizedKey =
              CARDIO_SCALE_KEY_NORMALIZER[key.toLowerCase()] ||
              key.toLowerCase();

            setFormData((prev) => ({
              ...prev,
              [normalizedKey]: data,
            }));

            setIsDirty(true);
          }}
          patientData={patientData}
        />
      );
    }

    switch (activeTab) {
      case "1":
        return (
          <ChiefComplaintsForm
            formData={formData}
            onValuesChange={onValuesChange}
            formType="cardio"
          />
        );

      case "2":
        return (
          <OpHistoryForm formData={formData} onValuesChange={onValuesChange}>
            <CardioHistory />
          </OpHistoryForm>
        );

      case "3":
        return (
          <VitalsForm formData={formData} onValuesChange={onValuesChange} />
        );

      case "4":
        return (
          <OPExaminationForm
            formData={formData}
            onValuesChange={onValuesChange}
          />
        );

      case "11":
        return (
          <OpFunctionalDiagnosis
            formData={formData}
            onValuesChange={onValuesChange}
            formType="cardio"
          />
        );

      case "12":
        return (
          <CardioTherapyForm
            formData={formData}
            onValuesChange={onValuesChange}
          />
        );

      case "13":
        return (
          <OpExerciseForm formData={formData} onValuesChange={onValuesChange} />
        );

      case "14":
        return (
          <ReviewNotes formData={formData} onValuesChange={onValuesChange} />
        );

      case "15":
        return <CardioSummary formData={formData} />;

      default:
        return null;
    }
  };

  return (
    <>
      <div className="op-assessment-tabs" style={{ background: "white" }}>
        {/* <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>
          Cardiopulmonary EMR
        </h2> */}
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={handleTabChange}
          moreIcon={null}
        />
      </div>
      <div
        className="op-assessment-panel"
        style={{
          height: "100vh",
          overflowY: "auto",
          backgroundColor: "#f6f5ff",
          borderRadius: "5px",
          border: "1px solid #e6e6e6",
          paddingBottom: "70px",
        }}
      >
        <div className="ia op-assessment-form-body">{renderTab()}</div>
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "100%",
          background: "#fff",
          padding: "10px 20px",
          borderTop: "1px solid #e6e6e6",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          zIndex: 1000,
        }}
      >
        <Button onClick={handleCloseWithCheck}>Cancel</Button>
        <Button type="primary" onClick={handleButton}>
          {activeTab === "15" ? "Save" : "Next"}
        </Button>
      </div>
    </>
  );
};

export default CardiopulmonaryForm;
